import React from "react";
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useGlobalLoading } from "@/context/GlobalLoadingContext";
import { toast } from 'sonner';
import { getIconComponent } from "@/lib/statusIconsMap";
import { STATUS_COLORS } from "@/lib/colorsMap";

export function StatusSelect({ task, showLabel = false, onStatusChange, onTaskChange, onRefreshActivities, className }) {
  const [open, setOpen] = useState(false);
  const { axiosInstance } = useAuth();
  const { showLoading, hideLoading } = useGlobalLoading();

  const statuses = React.useMemo(() => {
    if (!task?.availableStatuses || !Array.isArray(task.availableStatuses)) {
      return {};
    }

    return task.availableStatuses.reduce((acc, status) => {
      // Skip invalid status objects
      if (!status || typeof status !== 'object' || !status.id) {
        console.warn('Invalid status object found:', status);
        return acc;
      }

      // Get icon component with fallback
      const iconComponent = getIconComponent(status.icon) || getIconComponent('circle');
      
      // Get color with fallback to gray
      const colorKey = status.color || 'gray';
      const colorData = STATUS_COLORS[colorKey];
      const colorClass = colorData?.text || STATUS_COLORS.gray.text;

      return {
        ...acc,
        [status.id]: {
          name: status.name || 'Unknown Status',
          icon: iconComponent,
          color: colorClass
        }
      };
    }, {});
  }, [task?.availableStatuses]);

  // Early return if no valid statuses or task
  if (!task || Object.keys(statuses).length === 0) {
    return null;
  }

  // Get current status with fallback
  const currentStatusId = task.status?.id;
  const currentStatus = statuses[currentStatusId];
  
  // If current status is not found in available statuses, use the first available one
  const displayStatus = currentStatus || Object.values(statuses)[0];
  
  if (!displayStatus) {
    return null;
  }

  const performStatusChange = async (newStatus) => {
    // 1. Capture old value for rollback
    const oldStatus = task.status;
    
    // 2. Optimistically update UI immediately
    const optimisticTask = {
      ...task,
      status: { id: newStatus.id, name: newStatus.name }
    };
    
    if (onTaskChange) {
      onTaskChange(optimisticTask);
    }
    
    if (onStatusChange) {
      onStatusChange(newStatus);
    }
    
    // 3. Fire API call (don't await before UI update)
    try {
      showLoading();
      const { data: updatedTask } = await axiosInstance.patch(`/tasks/${task.organization.slug}/${task.key}`, {
        status: newStatus.id
      });
      
      // 4. Update with full response (includes completedAt changes)
      if (onTaskChange) {
        onTaskChange(updatedTask);
      }
      
      // 5. Refresh just activities (new activity was created)
      if (onRefreshActivities) {
        await onRefreshActivities();
      }
    } catch (error) {
      // 5. Rollback on error
      console.error('Error updating status:', error);
      if (onTaskChange) {
        onTaskChange({ ...task, status: oldStatus });
      }
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    } finally {
      hideLoading();
    }
  };

  const handleStatusChange = (newStatus) => {
    performStatusChange(newStatus);
  };

  return (
    <div className={className} onClick={(e) => e.stopPropagation()} >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="h-7 px-1.5 -ml-1.5 flex items-center gap-2">
              {React.createElement(displayStatus.icon, { className: `w-5 h-5 ${displayStatus.color}` })}
              {displayStatus.name}
            </Button>
            {showLabel}
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[200px] p-0" side="top" align="start">
          <Command>
            <CommandInput placeholder="Change status..." />
            <CommandList>
              <CommandEmpty>No status found.</CommandEmpty>
              <CommandGroup>
                {Object.entries(statuses).map(([key, value]) => (
                  <CommandItem
                    key={key}
                    value={key}
                    onSelect={() => {
                      handleStatusChange({
                        id: key,
                        name: value.name
                      });
                      setOpen(false);
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="h-7 px-1.5 flex items-center gap-2">
                        {React.createElement(value.icon, { className: `w-4 h-4 ${value.color}` })}
                        {value.name}
                      </Button>
                    </div>
                    {currentStatusId === key && (
                      <Check className="ml-auto h-4 w-4" />
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
